import { Color } from 'libs/end-to-end/src/types/branding/color';
import { Icon } from 'libs/end-to-end/src/types/branding/icon';
import { z } from 'zod';

export const DataGeneratorColumnDataType = z
  .enum(['categorical', 'numerical'])
  .describe('DataGeneratorColumnDataType');
export type DataGeneratorColumnDataType = z.infer<
  typeof DataGeneratorColumnDataType
>;

export const DataGeneratorColumnDataClass = z
  .enum(['dimension', 'controlVariable', 'metric', 'none'])
  .describe('DataGeneratorColumnDataClass');
export type DataGeneratorColumnDataClass = z.infer<
  typeof DataGeneratorColumnDataClass
>;

export const DataGeneratorColumnDataClassWithoutDimension =
  DataGeneratorColumnDataClass.exclude(['dimension']).describe(
    'DataGeneratorColumnDataClassWithoutDimension',
  );

export type DataGeneratorColumnDataClassWithoutDimension = z.infer<
  typeof DataGeneratorColumnDataClassWithoutDimension
>;

const DataGeneratorColumnDetailsBase = z.object({
  dataType: DataGeneratorColumnDataType,
  dataClass: DataGeneratorColumnDataClass,
  name: z.string(),
  description: z.string(),
  color: Color.nullish(),
  icon: Icon.nullish(),
  varyAcrossObservations: z.boolean(),
  varyAcrossPeriods: z.boolean(),
});

export const DataGeneratorCategoricalColumnDetails =
  DataGeneratorColumnDetailsBase.extend({
    dataType: z.literal('categorical'),
    values: z.array(z.string()).min(2),
  }).describe('DataGeneratorCategoricalColumnDetails');

export type DataGeneratorCategoricalColumnDetails = z.infer<
  typeof DataGeneratorCategoricalColumnDetails
>;

export const DataGeneratorNumericalColumnDetails =
  DataGeneratorColumnDetailsBase.extend({
    dataType: z.literal('numerical'),
    dataClass: DataGeneratorColumnDataClassWithoutDimension,
    min: z.number(),
    max: z.number(),
  }).describe('DataGeneratorNumericalColumnDetails');

export type DataGeneratorNumericalColumnDetails = z.infer<
  typeof DataGeneratorNumericalColumnDetails
>;

export const DataGeneratorColumnDetails = z
  .union([
    DataGeneratorCategoricalColumnDetails,
    DataGeneratorNumericalColumnDetails,
  ])
  .describe('DataGeneratorColumnDetails');

export type DataGeneratorColumnDetails = z.infer<
  typeof DataGeneratorColumnDetails
>;

export const DataGeneratorObservationPeriod = z
  .enum(['year', 'month', 'week', 'day'])
  .describe('DataGeneratorObservationPeriod');

export type DataGeneratorObservationPeriod = z.infer<
  typeof DataGeneratorObservationPeriod
>;

export const DataGeneratorSpecialColumns = z
  .object({
    subjectCount: z.number(),
    startDate: z.string(),
    endDate: z.string(),
    observationPeriod: DataGeneratorObservationPeriod.nullish().default('year'),
    numberOfObservationsPerSubjectPerPeriod: z.number().nullish().default(1),
    maxNumberOfSubjectsManaged: z.number().nullish().default(-1),
  })
  .describe('DataGeneratorSpecialColumns');

export type DataGeneratorSpecialColumns = z.infer<
  typeof DataGeneratorSpecialColumns
>;

export const RelationshipStatus = z
  .enum(['unconfigured', 'configured'])
  .describe('RelationshipStatus');

export type RelationshipStatus = z.infer<typeof RelationshipStatus>;

export const RelationshipCellSummary = z
  .object({
    status: RelationshipStatus,
    label: z.string().optional(),
  })
  .describe('RelationshipCellSummary');

export type RelationshipCellSummary = z.infer<typeof RelationshipCellSummary>;

export const RelationshipKind = z
  .enum([
    'numeric_numeric',
    'categorical_numeric',
    'numeric_categorical',
    'categorical_categorical',
  ])
  .describe('RelationshipKind');

export type RelationshipKind = z.infer<typeof RelationshipKind>;

const BaseRelationship = z
  .object({
    id: z.string(),
    columnA: z.string(), // column name
    columnB: z.string(), // column name
    kind: RelationshipKind,
    summary: RelationshipCellSummary,
  })
  .describe('BaseRelationship');

export type BaseRelationship = z.infer<typeof BaseRelationship>;

export const NumericNumericRelationshipMethod = z
  .enum(['pearson'])
  .describe('NumericNumericRelationshipMethod');

export type NumericNumericRelationshipMethod = z.infer<
  typeof NumericNumericRelationshipMethod
>;

export const NumericNumericRelationshipConfig = z
  .object({
    rho: z.number().min(-1).max(1),
    method: NumericNumericRelationshipMethod.default('pearson'),
  })
  .describe('NumericNumericRelationshipConfig');

export type NumericNumericRelationshipConfig = z.infer<
  typeof NumericNumericRelationshipConfig
>;

export const NumericNumericRelationship = BaseRelationship.extend({
  kind: z.literal('numeric_numeric'),
  config: NumericNumericRelationshipConfig,
}).describe('NumericNumericRelationship');

export type NumericNumericRelationship = z.infer<
  typeof NumericNumericRelationship
>;

export const NumericDistributionType = z
  .enum(['normal', 'lognormal', 'uniform', 'custom'])
  .describe('NumericDistributionType');

export type NumericDistributionType = z.infer<typeof NumericDistributionType>;

export const CategoricalToNumericDistributionConfig = z
  .object({
    distribution: NumericDistributionType,
    mean: z.number().optional(),
    stddev: z.number().optional(),
    min: z.number().optional(),
    max: z.number().optional(),
    noise: z.number().optional(), // optional extra variance
  })
  .describe('CategoricalToNumericDistributionConfig');

export type CategoricalToNumericDistributionConfig = z.infer<
  typeof CategoricalToNumericDistributionConfig
>;

export const DistributionsByCategory = z
  .record(
    z.string(), // category value
    CategoricalToNumericDistributionConfig,
  )
  .describe('DistributionsByCategory');

export type DistributionsByCategory = z.infer<typeof DistributionsByCategory>;

export const CategoricalToNumericRelationshipConfig = z
  .object({
    distributionsByCategory: DistributionsByCategory,
  })
  .describe('CategoricalToNumericRelationshipConfig');

export type CategoricalToNumericRelationshipConfig = z.infer<
  typeof CategoricalToNumericRelationshipConfig
>;

export const CategoricalToNumericRelationship = BaseRelationship.extend({
  kind: z.literal('categorical_numeric'),
  config: CategoricalToNumericRelationshipConfig,
}).describe('CategoricalToNumericRelationship');

export type CategoricalToNumericRelationship = z.infer<
  typeof CategoricalToNumericRelationship
>;

export const NumericToCategoricalRelationshipParameters = z
  .object({
    // Logistic example
    slope: z.number().optional(),
    intercept: z.number().optional(),

    // Threshold / piecewise
    breakpoints: z.array(z.number()).optional(),
    probabilities: z.array(z.array(z.number())).optional(),
  })
  .describe('NumericToCategoricalRelationshipParameters');

export type NumericToCategoricalRelationshipParameters = z.infer<
  typeof NumericToCategoricalRelationshipParameters
>;

export const NumericToCategoricalRelationshipModelType = z
  .enum(['logistic', 'threshold', 'piecewise'])
  .describe('NumericToCategoricalRelationshipModelType');

export type NumericToCategoricalRelationshipModelType = z.infer<
  typeof NumericToCategoricalRelationshipModelType
>;

export const NumericToCategoricalRelationshipConfig = z
  .object({
    modelType: NumericToCategoricalRelationshipModelType,
    parameters: NumericToCategoricalRelationshipParameters,
    categoryOrder: z.array(z.string()),
  })
  .describe('NumericToCategoricalRelationshipConfig');

export type NumericToCategoricalRelationshipConfig = z.infer<
  typeof NumericToCategoricalRelationshipConfig
>;

export const NumericToCategoricalRelationship = BaseRelationship.extend({
  kind: z.literal('numeric_categorical'),
  config: NumericToCategoricalRelationshipConfig,
}).describe('NumericToCategoricalRelationship');

export type NumericToCategoricalRelationship = z.infer<
  typeof NumericToCategoricalRelationship
>;

export const CategoricalCategoricalRelationshipLockedCell = z
  .object({
    row: z.string(),
    column: z.string(),
  })
  .describe('CategoricalCategoricalRelationshipLockedCell');

export type CategoricalCategoricalRelationshipLockedCell = z.infer<
  typeof CategoricalCategoricalRelationshipLockedCell
>;

export const CategoricalCategoricalRelationshipConditioning = z
  .enum(['A_given_B', 'B_given_A'])
  .describe('CategoricalCategoricalRelationshipConditioning');

export type CategoricalCategoricalRelationshipConditioning = z.infer<
  typeof CategoricalCategoricalRelationshipConditioning
>;

export const CategoricalCategoricalRelationshipMatrixInner = z
  .record(
    z.string(), // category of conditioned variable
    z.number().min(0).max(1),
  )
  .describe('CategoricalCategoricalRelationshipMatrixInner');

export type CategoricalCategoricalRelationshipMatrixInner = z.infer<
  typeof CategoricalCategoricalRelationshipMatrixInner
>;

export const CategoricalCategoricalRelationshipMatrix = z
  .record(
    z.string(), // category of conditioning variable
    CategoricalCategoricalRelationshipMatrixInner,
  )
  .describe('CategoricalCategoricalRelationshipMatrix');

export type CategoricalCategoricalRelationshipMatrix = z.infer<
  typeof CategoricalCategoricalRelationshipMatrix
>;

export const CategoricalCategoricalRelationshipConfig = z
  .object({
    conditioning: CategoricalCategoricalRelationshipConditioning,
    matrix: CategoricalCategoricalRelationshipMatrix,
    lockedCells: z
      .array(CategoricalCategoricalRelationshipLockedCell)
      .optional(),
  })
  .describe('CategoricalCategoricalRelationshipConfig');

export type CategoricalCategoricalRelationshipConfig = z.infer<
  typeof CategoricalCategoricalRelationshipConfig
>;

export const CategoricalCategoricalRelationship = BaseRelationship.extend({
  kind: z.literal('categorical_categorical'),
  config: CategoricalCategoricalRelationshipConfig,
}).describe('CategoricalCategoricalRelationship');

export type CategoricalCategoricalRelationship = z.infer<
  typeof CategoricalCategoricalRelationship
>;

export const DataGeneratorRelationship = z
  .union([
    NumericNumericRelationship,
    CategoricalToNumericRelationship,
    NumericToCategoricalRelationship,
    CategoricalCategoricalRelationship,
  ])
  .describe('DataGeneratorRelationship');

export type DataGeneratorRelationship = z.infer<
  typeof DataGeneratorRelationship
>;
export const DataGeneratorSimpleRelationshipValue = z
  .enum([
    'strongNegative',
    'moderateNegative',
    'weakNegative',
    'neutral',
    'weakPositive',
    'moderatePositive',
    'strongPositive',
  ])
  .describe('DataGeneratorSimpleRelationshipValue');
export type DataGeneratorSimpleRelationshipValue = z.infer<
  typeof DataGeneratorSimpleRelationshipValue
>;
export const DataGeneratorSimpleRelationship = z
  .object({
    columnA: z.string(),
    columnB: z.string(),
    value: DataGeneratorSimpleRelationshipValue,
  })
  .describe('DataGeneratorSimpleRelationship');

export type DataGeneratorSimpleRelationship = z.infer<
  typeof DataGeneratorSimpleRelationship
>;

export const TimeBoundRelationshipSet = z
  .object({
    startDate: z.string(),
    endDate: z.string(),
    relationships: z.array(DataGeneratorSimpleRelationship),
  })
  .describe('TimeBoundRelationshipSet');

export type TimeBoundRelationshipSet = z.infer<typeof TimeBoundRelationshipSet>;

export const TimeBoundBiasSetFilter = z
  .object({
    column: z.string(),
    values: z.array(z.string()),
  })
  .describe('TimeBoundBiasSetFilter');

export type TimeBoundBiasSetFilter = z.infer<typeof TimeBoundBiasSetFilter>;

export const TimeBoundBiasSetBiasTypes = ['generic'] as const;

export const TimeBoundBiasSetBiasType = z
  .enum(TimeBoundBiasSetBiasTypes)
  .describe('TimeBoundBiasSetBiasType');

export type TimeBoundBiasSetBiasType = z.infer<typeof TimeBoundBiasSetBiasType>;

export const TimeBoundBiasSet = z
  .object({
    startDate: z.string(),
    endDate: z.string(),
    dimension: z.string(),
    level: z.string(),
    metric: z.string(),
    filters: z.array(TimeBoundBiasSetFilter),
    biasType: TimeBoundBiasSetBiasType,
  })
  .describe('TimeBoundBiasSet');

export type TimeBoundBiasSet = z.infer<typeof TimeBoundBiasSet>;

export const DataGeneratorInput = z
  .object({
    name: z.string(),
    description: z.string(),
    randomSeed: z.string().nullish(),
    columns: z.array(DataGeneratorColumnDetails),
    relationships: z.array(DataGeneratorSimpleRelationship).optional(),
    relationshipSets: z.array(TimeBoundRelationshipSet).optional(),
    biasSets: z.array(TimeBoundBiasSet).optional(),
    specialColumnsConfig: DataGeneratorSpecialColumns,
  })
  .describe('DataGeneratorInput');

export type DataGeneratorInput = z.infer<typeof DataGeneratorInput>;

export const DataGeneratorStateMachineInput = z.object({
  config: DataGeneratorInput,
  runId: z.string(),
});

export type DataGeneratorStateMachineInput = z.infer<
  typeof DataGeneratorStateMachineInput
>;

export type DataGeneratorRows = readonly {
  id: number;
  name: string;
  description: string;
  model: DataGeneratorColumnDetails;
}[];

export type CategoricalValueRows = readonly {
  id: number;
  value: string;
}[];
